/**
 * Menu action resolution — the pure half of wiring the drill-down menu to the
 * real app. A leaf in the menu tree carries an `action` string; this turns that
 * string into a typed instruction the MenuSheet can carry out against the real
 * stores (open this sheet, open a focused in-menu control, set this palette, arm
 * this tool, run this command), or classifies it as a parent (drill in), planned,
 * or not yet wired.
 *
 * Keeping resolution pure and separate from the React sheet means the whole tree
 * can be checked in a unit test: every leaf either does something real or is
 * honestly flagged planned, never a dead button.
 */
import type { SheetName } from '@/store/shell';
import type { Palette } from '@/store';
import type { MenuLeaf } from './menuTree';

/** Sheet names the menu can open directly. */
const SHEET_ACTIONS = new Set<string>([
  'mode',
  'route',
  'waypoint',
  'planning',
  'conditions',
  'weathertable',
  'tides',
  'ais',
  'forecast',
  'layers',
  'alarms',
  'settings',
  'pilotage',
  'tracks',
  'note',
  'search',
  'eblvrm',
  'pilotageplans',
  'pilotagecommunity',
]);

/** Commands the menu can run that aren't a sheet or a simple setter. */
export type MenuCommand =
  | 'set_destination'
  | 'measure'
  | 'range_rings'
  | 'ebl_vrm'
  | 'anchor_watch'
  | 'mob'
  | 'distress'
  | 'provider'
  | 'passage_wizard'
  | 'weather_overlay';

/**
 * The sheet a command opens, if any. A handful of commands don't just arm a tool
 * or drop a mark, they hand the user over to a whole sheet (the passage-plan
 * wizard opens Plotting, the anchor watch opens Alarms). The menu needs to know
 * this so it hands over to that sheet instead of closing the menu ON TOP of it,
 * which slammed the just-opened sheet shut and made the button look dead
 * (fb-ms1hh1cn-6). Returns null for commands that leave no sheet open.
 */
export function commandOpensSheet(command: MenuCommand): SheetName | null {
  switch (command) {
    case 'passage_wizard':
      return 'planning';
    case 'anchor_watch':
      return 'alarms';
    default:
      return null;
  }
}

export type ResolvedMenuAction =
  | { kind: 'parent' } // has children: drill in
  | { kind: 'sheet'; sheet: SheetName }
  | { kind: 'control'; control: string } // focused control rendered inside the menu
  | { kind: 'palette'; palette: Palette }
  | { kind: 'tool'; tool: string }
  | { kind: 'command'; command: MenuCommand; arg?: string }
  | { kind: 'planned' }
  | { kind: 'unknown' };

/** Resolve a leaf into a typed action. Children take precedence (drill in). */
export function resolveMenuAction(leaf: MenuLeaf): ResolvedMenuAction {
  if (leaf.children && leaf.children.length) return { kind: 'parent' };
  if (leaf.planned) return { kind: 'planned' };

  const action = leaf.action;
  if (!action) return { kind: 'unknown' };

  // Prefixed actions: "control:provider", "palette:day", "provider:vector", "tool:vrm".
  const colon = action.indexOf(':');
  if (colon !== -1) {
    const head = action.slice(0, colon);
    const arg = action.slice(colon + 1);
    if (head === 'control') return { kind: 'control', control: arg };
    if (head === 'palette') {
      if (arg === 'day' || arg === 'dusk' || arg === 'night')
        return { kind: 'palette', palette: arg };
      return { kind: 'planned' }; // palette:auto and the like
    }
    if (head === 'provider') return { kind: 'command', command: 'provider', arg };
    if (head === 'tool') return { kind: 'tool', tool: arg };
    return { kind: 'unknown' };
  }

  // Bare tool ids.
  if (action === 'measure') return { kind: 'command', command: 'measure' };
  if (action === 'rangerings') return { kind: 'command', command: 'range_rings' };

  // Bare command ids that aren't sheets.
  if (action === 'passage-wizard') return { kind: 'command', command: 'passage_wizard' };
  if (action === 'weather_overlay') return { kind: 'command', command: 'weather_overlay' };
  if (action === 'set_destination') return { kind: 'command', command: 'set_destination' };
  if (action === 'anchorwatch') return { kind: 'command', command: 'anchor_watch' };
  if (action === 'mob') return { kind: 'command', command: 'mob' };
  if (action === 'distress') return { kind: 'command', command: 'distress' };

  // Sheets.
  if (SHEET_ACTIONS.has(action)) return { kind: 'sheet', sheet: action as SheetName };

  return { kind: 'unknown' };
}

/** A short verb describing what selecting the leaf will do, for accessibility/labels. */
export function actionVerb(a: ResolvedMenuAction): string {
  switch (a.kind) {
    case 'parent':
      return 'Open submenu';
    case 'sheet':
      return 'Open';
    case 'control':
      return 'Open';
    case 'palette':
      return `Set ${a.palette} palette`;
    case 'tool':
      return 'Select tool';
    case 'command':
      return 'Run';
    case 'planned':
      return 'Coming soon';
    default:
      return '';
  }
}
